import { ApiOptions, RestOptions, User } from '@csp/csp-common-model';
import { toRestOptions } from '@csp/csp-fe-auth';
import { ConsentRestServiceV1 } from '@csp/dmdp-api-client';
import {
  ConsentActionCriteriaFieldV1,
  ConsentActionQueryOperatorV1,
  ConsentActionQueryTypeV1,
  ConsentActionV1,
  ConsentActionsV1,
  ConsentArtifactCriteriaFieldV1,
  ConsentArtifactQueryTypeV1,
  ConsentArtifactV1,
} from '@csp/dmdp-api-consent-dto';
import { ConsentActionStatusValue } from '../model/ConsentAction';

const approveConsentV2 = async (
  artifactCode: string,
  localTimestamp: string,
  apiOptions?: ApiOptions,
): Promise<ConsentActionV1> =>
  await ConsentRestServiceV1.postApprovalActionV1(toRestOptions(apiOptions), {
    artifactCode,
    localTimestamp,
  });

const withdrawApprovedConsentsByCmsConsentType = async (
  cmsConsentType: string,
  localTimestamp: string,
  user: User,
  options?: Record<string, string>,
  apiOptions?: ApiOptions,
): Promise<ConsentActionV1[]> => {
  const approvedArtifacts = await getApprovedArtifactsWithSameCmsConsentType(cmsConsentType, user, apiOptions);
  if (approvedArtifacts.length > 0) {
    return Promise.all(
      approvedArtifacts.map(a =>
        ConsentRestServiceV1.postWithdrawActionV1(toRestOptions(apiOptions), {
          artifactCode: a.artifactCode,
          localTimestamp,
          options,
        }),
      ),
    );
  }
  return [];
};

const withdrawConsent = async (
  artifactCode: string,
  localTimestamp: string,
  cmsConsentType: string,
  user: User,
  options?: Record<string, string>,
  apiOptions?: ApiOptions,
): Promise<ConsentActionV1> => {
  await withdrawApprovedConsentsByCmsConsentType(cmsConsentType, localTimestamp, user, options, apiOptions);

  return ConsentRestServiceV1.postWithdrawActionV1(toRestOptions(apiOptions), {
    artifactCode,
    localTimestamp,
    options,
  });
};

const rejectConsent = async (
  artifactCode: string,
  localTimestamp: string,
  cmsConsentType: string,
  user: User,
  options?: Record<string, string>,
  apiOptions?: ApiOptions,
): Promise<ConsentActionV1> => {
  await withdrawApprovedConsentsByCmsConsentType(cmsConsentType, localTimestamp, user, options, apiOptions);

  return ConsentRestServiceV1.postRejectActionV1(toRestOptions(apiOptions), {
    artifactCode,
    localTimestamp,
  });
};

const getApprovedConsentActions = async (user: User, apiOptions?: RestOptions): Promise<ConsentActionsV1> =>
  ConsentRestServiceV1.queryConsentActionsV1(toRestOptions(apiOptions), {
    criterion: {
      operator: ConsentActionQueryOperatorV1.AND,
      criteria: [
        {
          fieldCriterion: {
            field: ConsentActionCriteriaFieldV1.STATUS,
            type: ConsentActionQueryTypeV1.EQ,
            values: [ConsentActionStatusValue.APPROVED],
          },
        },
        {
          fieldCriterion: {
            field: ConsentActionCriteriaFieldV1.USER_ID,
            type: ConsentActionQueryTypeV1.EQ,
            values: [user.userId],
          },
        },
      ],
    },
  });

const getApprovedArtifactsWithSameCmsConsentType = async (
  consentType: string,
  user: User,
  apiOptions?: ApiOptions,
): Promise<ConsentArtifactV1[]> => {
  const approvedArtifacts = await getApprovedConsentActions(user, toRestOptions(apiOptions));

  const allArtifactCodes = approvedArtifacts.actions.map(a => a.artifactCode);

  if (allArtifactCodes.length > 0) {
    const allArtifacts = await ConsentRestServiceV1.queryArtifacts(toRestOptions(apiOptions), {
      criterion: {
        fieldCriterion: {
          field: ConsentArtifactCriteriaFieldV1.ARTIFACT_CODE,
          type: ConsentArtifactQueryTypeV1.IN,
          values: allArtifactCodes,
        },
      },
    });
    return allArtifacts.artifacts.filter(a => {
      const { CONFIG_V2 } = a.meta ?? {};
      const { cmsConsentType } = CONFIG_V2 ?? {};
      return consentType === cmsConsentType;
    });
  }
  return [];
};

export const ConsentActionService = {
  approveConsentV2,
  rejectConsent,
  withdrawConsent,
  getApprovedConsentActions,
  getApprovedArtifactsWithSameCmsConsentType,
};
